import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Container,
  Card,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaHistory } from "react-icons/fa";
import moment from "moment";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";

const LeaveViewModal = ({ show, handleClose, leaveId, leave }) => {
  const [leaveLogs, setLeaveLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !leaveId) return;

    const fetchLeaveLogs = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${BASE_URL}/users/leaves-logs/${leaveId}/`,
          { headers: HEADERS },
        );

        setLeaveLogs(response.data);
      } catch (err) {
        console.error("Error fetching leave logs:", err);
        setError("Failed to load leave logs.");
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveLogs();
  }, [show, leaveId]);

  const getBadgeColor = (status) => {
    switch (status) {
      case "Approved":
        return "#28a745";
      case "Submitted":
        return "#6c757d";
      case "Rejected":
        return "#dc3545";
      default:
        return "#343a40";
    }
  };

  const renderChanges = (changes) => {
    if (!changes || typeof changes !== "object") return "—";

    const validChanges = Object.entries(changes).filter(
      ([, v]) => v?.new !== null && v?.new !== "" && v?.new !== undefined,
    );

    if (validChanges.length === 0) return "—";

    return (
      <ul className="mb-0 ps-3">
        {validChanges.map(([field, value], idx) => (
          <li key={idx}>
            <strong>{field}</strong>:{" "}
            <span className="text-muted">{value.previous ?? "—"}</span> →{" "}
            <span className="text-success">{value.new}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      scrollable
      dialogClassName="custom-modern-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100 d-flex justify-content-between align-items-center">
          <span className="fw-semibold">📄 Leave Details</span>

          <span
            className="badge"
            style={{
              backgroundColor: getBadgeColor(leave?.status),
              padding: "6px 12px",
              color: "#fff",
            }}
          >
            {leave?.status}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading leave data...</p>
          </div>
        ) : error ? (
          <p className="text-danger text-center">{error}</p>
        ) : (
          <Container fluid>
            <Card className="mb-3 shadow-sm">
              <Card.Body className="small">
                <Row>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Start Date:</strong>{" "}
                      {moment(leave?.start_date).format("DD-MM-YYYY")}
                    </p>

                    <p className="mb-1">
                      <strong>End Date:</strong>{" "}
                      {moment(leave?.end_date).format("DD-MM-YYYY")}
                    </p>

                    <p className="mb-1">
                      <strong>Leave Type:</strong> {leave?.leave_type}
                    </p>

                    <p className="mb-1">
                      <strong>Created By:</strong> {leave?.created_by}
                    </p>
                  </Col>

                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Status:</strong> {leave?.status}
                    </p>

                    <p className="mb-1">
                      <strong>Reason:</strong> {leave?.reason}
                    </p>

                    <p className="mb-1">
                      <strong>Created At:</strong>{" "}
                      {leave?.created_at
                        ? moment(leave.created_at).format(
                            "DD MMM YYYY, hh:mm A",
                          )
                        : "—"}
                    </p>

                    <p className="mb-1">
                      <strong>Remark:</strong>{" "}
                      {leave?.approved_remark || leave?.rejected_remark || "—"}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="shadow-sm">
              <Card.Body>
                <h5 className="text-primary mb-3">
                  <FaHistory className="me-2" />
                  Leave Activity Log
                </h5>

                {leaveLogs?.logs?.length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table className="inventory-table w-100">
                      <thead>
                        <tr>
                          <th style={{ minWidth: "150px" }}>Updated By</th>
                          <th style={{ minWidth: "170px" }}>Date & Time</th>
                          <th style={{ minWidth: "120px" }}>Action</th>
                          <th style={{ minWidth: "250px" }}>Changes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaveLogs.logs.map((log, idx) => (
                          <tr key={idx}>
                            <td>{log.updated_by || "—"}</td>
                            <td>
                              {moment(log.updated_on).format(
                                "DD-MM-YYYY hh:mm A",
                              )}
                            </td>
                            <td>
                              <Badge bg="secondary">{log.action}</Badge>
                            </td>
                            <td>{renderChanges(log.changes)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-center mb-0">
                    No leave activity available.
                  </p>
                )}
              </Card.Body>
            </Card>
          </Container>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LeaveViewModal;
